import { ArrowUpRight } from "lucide-react";
import { useT, useI18n } from "@/lib/i18n";

const COPY = {
  de: {
    location_overline: "Standort · Mels",
    location_address: "Zeughausstrasse 12 · 8887",
    p1: (
      <>
        Die <span className="text-white">Fritz Kamm AG</span> zählt zu den
        führenden Galvanik-Unternehmen in der Schweiz. In Mels veredeln wir
        Bauteile aus Stahl, Edelstahl und Buntmetallen mit elf bewährten
        Verfahren — vom Verzinken bis zum CNS-Elektropolieren.
      </>
    ),
    p2: (
      <>
        Unser Leitziel <span className="text-white">„Qualität und Termin"</span>
        {" "}ordnet alles unter: jeden Prozessschritt, jede Charge, jede
        Auslieferung. Wir arbeiten nach Swiss Safety Center zertifizierten
        Vorgaben (ISO 9001 / ISO 14001) und reduzieren CO₂-Emissionen
        kontinuierlich.
      </>
    ),
    p3:
      "Technischen Support, Oberflächenqualitätsprüfungen, Beschriftungen und Transportleistungen bieten wir ergänzend an. Unsere Fachkräfte stehen Ihnen jederzeit persönlich zur Verfügung.",
    cta: "Sprechen Sie mit unserem Team",
    stats: [
      ["11", "Veredelungsverfahren"],
      ["1968", "in Mels SG · seit über 55 Jahren"],
      ["100%", "Swiss Quality"],
      ["ISO", "9001 & 14001"],
    ],
    img_alt: "Galvanik bei Fritz Kamm AG in Mels",
  },
  en: {
    location_overline: "Location · Mels",
    location_address: "Zeughausstrasse 12 · 8887",
    p1: (
      <>
        <span className="text-white">Fritz Kamm AG</span> is among
        Switzerland's leading electroplating companies. In Mels we finish
        parts in steel, stainless steel and non-ferrous metals using eleven
        proven processes — from electrogalvanizing to stainless-steel
        electropolishing.
      </>
    ),
    p2: (
      <>
        Our guiding principle <span className="text-white">"quality and on-time delivery"</span>
        {" "}defines everything: every process step, every batch, every
        shipment. We operate to Swiss Safety Center certified standards
        (ISO 9001 / ISO 14001) and continuously reduce CO₂ emissions.
      </>
    ),
    p3:
      "We additionally offer technical support, surface-quality inspection, marking and logistics. Our specialists are available to you personally at any time.",
    cta: "Talk to our team",
    stats: [
      ["11", "Finishing processes"],
      ["1968", "in Mels SG · 55+ years"],
      ["100%", "Swiss Quality"],
      ["ISO", "9001 & 14001"],
    ],
    img_alt: "Electroplating at Fritz Kamm AG in Mels",
  },
  fr: {
    location_overline: "Site · Mels",
    location_address: "Zeughausstrasse 12 · 8887",
    p1: (
      <>
        <span className="text-white">Fritz Kamm SA</span> figure parmi les
        principales entreprises de galvanoplastie en Suisse. À Mels, nous
        traitons des pièces en acier, acier inoxydable et métaux non ferreux
        selon onze procédés éprouvés — du zingage classique au polissage
        électrolytique de l'inox.
      </>
    ),
    p2: (
      <>
        Notre exigence <span className="text-white">« qualité et délais »</span>
        {" "}guide chaque opération : chaque étape, chaque lot, chaque
        livraison. Nous travaillons selon les standards Swiss Safety Center
        (ISO 9001 / ISO 14001) et réduisons continuellement nos émissions de CO₂.
      </>
    ),
    p3:
      "Nous proposons également un support technique, le contrôle qualité des surfaces, le marquage et la logistique. Nos spécialistes sont à votre disposition à tout moment.",
    cta: "Parler à notre équipe",
    stats: [
      ["11", "Procédés de finition"],
      ["1968", "à Mels SG · depuis 55+ ans"],
      ["100%", "Swiss Quality"],
      ["ISO", "9001 & 14001"],
    ],
    img_alt: "Galvanoplastie chez Fritz Kamm SA à Mels",
  },
  it: {
    location_overline: "Sede · Mels",
    location_address: "Zeughausstrasse 12 · 8887",
    p1: (
      <>
        <span className="text-white">Fritz Kamm SA</span> è tra le principali
        aziende galvaniche della Svizzera. A Mels trattiamo componenti in
        acciaio, acciaio inox e metalli non ferrosi con undici procedimenti
        consolidati — dalla zincatura classica all'elettrolucidatura
        dell'inox.
      </>
    ),
    p2: (
      <>
        Il nostro principio guida <span className="text-white">«qualità e puntualità»</span>
        {" "}orienta ogni operazione: ogni fase di processo, ogni lotto, ogni
        consegna. Lavoriamo secondo gli standard certificati Swiss Safety
        Center (ISO 9001 / ISO 14001) e riduciamo costantemente le emissioni
        di CO₂.
      </>
    ),
    p3:
      "Offriamo inoltre supporto tecnico, controllo qualità delle superfici, marcatura e logistica. I nostri specialisti sono a Sua disposizione personalmente in ogni momento.",
    cta: "Parli con il nostro team",
    stats: [
      ["11", "Procedimenti di finitura"],
      ["1968", "a Mels SG · da oltre 55 anni"],
      ["100%", "Swiss Quality"],
      ["ISO", "9001 & 14001"],
    ],
    img_alt: "Galvanica presso Fritz Kamm SA a Mels",
  },
};

export const About = () => {
  const t = useT();
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;
  return (
    <section
      id="ueber"
      data-testid="about-section"
      className="relative bg-[#0E0E10] py-24 md:py-36 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-6 reveal">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src="/media/Trommelverzinken-blau-1030x684.jpg"
                alt={c.img_alt}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <div>
                  <div className="overline text-zinc-300 mb-2">{c.location_overline}</div>
                  <div className="font-display text-white text-2xl tracking-tight">
                    {c.location_address}
                  </div>
                </div>
                <div className="hidden md:flex items-center justify-center w-12 h-12 border border-white/30 backdrop-blur-md bg-white/5">
                  <ArrowUpRight className="w-4 h-4 text-white" strokeWidth={1.5} />
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <div className="overline reveal mb-5">{t("sections.ueber_overline", "— Über uns")} · 01 / 07</div>
            <h2 className="reveal reveal-delay-1 font-display text-4xl sm:text-5xl lg:text-[64px] font-semibold tracking-tight leading-[0.95] text-white">
              {t("sections.ueber_title_1", "Eine Schweizer Adresse")}
              <br />
              <span className="text-zinc-500">{t("sections.ueber_title_2", "für Galvanik.")}</span>
            </h2>

            <div className="reveal reveal-delay-2 mt-8 space-y-5 text-zinc-300 text-base md:text-lg leading-relaxed">
              <p>{c.p1}</p>
              <p className="text-zinc-400">{c.p2}</p>
              <p className="text-zinc-400">{c.p3}</p>
            </div>

            <div className="reveal reveal-delay-3 mt-12 grid grid-cols-2 gap-px bg-white/10 border border-white/10">
              {c.stats.map(([k, v]) => (
                <div key={v} className="bg-[#0E0E10] p-6 md:p-8">
                  <div className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-white">
                    {k}
                  </div>
                  <div className="overline mt-2">{v}</div>
                </div>
              ))}
            </div>

            <a
              href="#kontakt"
              data-testid="about-cta"
              className="reveal reveal-delay-4 mt-10 inline-flex items-center gap-2 text-white text-sm font-semibold link-underline"
            >
              {c.cta}
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
